import { create, type StateCreator } from 'zustand';
import { infermedicaApi } from '@/API/modules/infermedicaApi';
import { LOCAL_STORAGE_KEYS } from '@/utils/constants';
import type { IInfermedicaSurveySlice } from './types';
import type { IInfemedicaKnowledgeItem } from '@/types/infermedica';

export type TInfermedicaSurveySlice = IInfermedicaSurveySlice;

const infermedicaInitialState: Pick<
  IInfermedicaSurveySlice,
  | 'surveyId'
  | 'token'
  | 'refreshToken'
  | 'loading'
  | 'currentQuestion'
  | 'results'
> = {
  surveyId: null,
  token: null,
  refreshToken: null,
  loading: false,
  currentQuestion: null,
  results: null,
};

export const infermedicaSurveySlice: StateCreator<
  TInfermedicaSurveySlice,
  [],
  [],
  TInfermedicaSurveySlice
> = (set, get) => ({
  ...infermedicaInitialState,

  setAuthToken: (token) => {
    set({ token });
    if (token) {
      window.localStorage.setItem(LOCAL_STORAGE_KEYS.INFERMEDICA_TOKEN, token);
    } else {
      window.localStorage.removeItem(LOCAL_STORAGE_KEYS.INFERMEDICA_TOKEN);
    }
  },

  clearInfermedicaState: () => {
    localStorage.removeItem(LOCAL_STORAGE_KEYS.INFERMEDICA_TOKEN);
    localStorage.removeItem(LOCAL_STORAGE_KEYS.INFERMEDICA_REFRESH_TOKEN);
    set(infermedicaInitialState);
  },

  setInfermedicaResultTest: () => {
    set({
      results: {
        specialist: {
          id: 'sp_1',
          name: 'General Practice',
        },
        recommended_specialist: 'General Practice',
      },
      surveyId: 'c04ba03a-196f-425a-b43b-ce3bb68c8618',
    });
  },

  getStartTestInfermedica: async () => {
    await get().startSurvey();
    await get().getQuestion(0);
  },

  startSurvey: async () => {
    set({ loading: true });

    try {
      const { data } = await infermedicaApi.getAccessTokenAndSurvey();
      const { access_token, refresh_token, survey_id } = data.data;

      window.localStorage.setItem(
        LOCAL_STORAGE_KEYS.INFERMEDICA_TOKEN,
        access_token,
      );
      window.localStorage.setItem(
        LOCAL_STORAGE_KEYS.INFERMEDICA_REFRESH_TOKEN,
        refresh_token,
      );

      set({
        token: access_token,
        refreshToken: refresh_token,
        surveyId: survey_id,
      });
    } finally {
      set({ loading: false });
    }
  },

  getSurveyId: () => {
    const surveyId = get().surveyId;
    if (!surveyId) throw new Error('Survey ID is not available');
    return surveyId;
  },

  setCurrentQuestion: (question) => set({ currentQuestion: question }),

  getResults: async () => {
    const surveyId = get().getSurveyId();
    set({ currentQuestion: null });
    const { data: results } = await infermedicaApi.getResults(surveyId);
    set({ results });
  },

  getQuestion: async (questionIndex: number = 0) => {
    const surveyId = get().getSurveyId();
    set({ loading: true });

    try {
      const { data: currentQuestion, status } =
        await infermedicaApi.getIndexedQuestion(surveyId, questionIndex);

      if (status === 204) {
        await get().getResults();
      } else {
        get().setCurrentQuestion(currentQuestion);
      }
    } finally {
      set({ loading: false });
    }
  },

  giveAnswer: async (questionIndex, answer) => {
    const currentQuestion = get().currentQuestion;
    if (!currentQuestion) throw new Error('Current question is not available');

    const surveyId = get().getSurveyId();
    set({ loading: true });

    try {
      const { data: nextQuestion, status } =
        await infermedicaApi.giveIndexedAnswer(surveyId, questionIndex, answer);

      if (status === 204) {
        await get().getResults();
      } else {
        get().setCurrentQuestion(nextQuestion);
      }
    } finally {
      set({ loading: false });
    }
  },

  clearResult: () => set({ results: null }),
});

interface IInfermedicaKnowledgeBaseState {
  loading: boolean;
  knowledgeItems: IInfemedicaKnowledgeItem[];
  getKnowledgeItems: (endpoint: string, query: string) => Promise<void>;
}

export const useInfermedicaKnowledgeBase =
  create<IInfermedicaKnowledgeBaseState>((set) => ({
    loading: false,
    knowledgeItems: [],
    getKnowledgeItems: async (endpoint, query) => {
      set({ loading: true, knowledgeItems: [] });

      try {
        const { data } = await infermedicaApi.getKnowledgeEndpoint({
          url: endpoint,
          query,
        });

        set({ knowledgeItems: data.items });
      } finally {
        set({ loading: false });
      }
    },
  }));
